/** Review rows include projected and persisted Visits; their panel opens the existing Visit surface. */
import { ActivityEntityName, EntityViewDisplay } from '@jms/model';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { defineStatusTag } from '../shared/define.status.tag';
import { PlannedWorkCol } from '../shared/planned.work.board.columns';
import { ITooltipParams } from 'ag-grid-enterprise';
import { VisitApprovalReviewBoardRow, VisitApprovalReviewStatus } from './visit.approval.board.model';
import { ApprovalProgressComponent } from './field.renderers/approval.progress/approval.progress.component';

export const visitApprovalReviewManifest = registerManifest<VisitApprovalReviewBoardRow>({
  entity: ActivityEntityName.VisitApprovalReview,
  presentation: {
    tags: {
      approvalStatus: defineStatusTag<VisitApprovalReviewStatus, VisitApprovalReviewBoardRow>({
        Awaiting: 'neutral',
        Approved: 'success',
        Changed: 'warning',
        Unavailable: 'error',
      }),
    },
  },
  board: {
    supportedDisplays: [EntityViewDisplay.Table],
    defaults: {
      paging: 'count',
      header: { title: 'Visit approvals', hideAdd: true, showPaginator: true },
      table: {
        fillerColumn: true,
        // The Approval column itself goes live rather than gaining a second Progress column beside it:
        // a row has ONE approval standing, and two columns showing it would disagree the moment the
        // fetched one lagged the running operation.
        overrideCellRenderer: (_context, _metadata, field, renderer) =>
          field.name === 'approvalStatus'
            ? { ...renderer, cellRenderer: ApprovalProgressComponent }
            : renderer,
        overrideColumnDefs: (_context, columns) => {
          const order = [
            'startDate', 'servicePlan', 'serviceLine', 'serviceType', 'serviceScope',
            'site', 'approvalStatus', 'approvalUnavailableReason', 'taskCount',
          ];
          const presented = columns.map((column) =>
            column.field === 'approvalStatus'
              ? {
                  ...column,
                  // Reason has its own column now; the tooltip just repeats it for a truncated cell.
                  tooltipValueGetter: ({ data }: ITooltipParams<VisitApprovalReviewBoardRow>) =>
                    data?.approvalUnavailableReason || data?.approvalStatus || '',
                }
              : column,
          );
          const ordered = presented
            .filter(column => order.includes(column.field))
            .sort((left, right) => order.indexOf(left.field) - order.indexOf(right.field));
          let index = 0;
          // Keep selection/menu chrome in the board's own positions.
          return presented.map(column => order.includes(column.field) ? ordered[index++] : column);
        },
        columns: {
          byField: {
            startDate: { headerName: 'Date / time', ...PlannedWorkCol.dateTime },
            site: PlannedWorkCol.site,
            customer: PlannedWorkCol.customer,
            servicePlan: PlannedWorkCol.title,
            serviceLine: PlannedWorkCol.line,
            serviceType: { headerName: 'Service type', ...PlannedWorkCol.tag },
            serviceScope: { headerName: 'Service scope', ...PlannedWorkCol.tag },
            approvalStatus: { headerName: 'Approval', ...PlannedWorkCol.status },
            approvalUnavailableReason: { headerName: 'Reason' },
          },
        },
      },
    },
  },
});
